/**
 * Security monitoring hook
 * Handles visibility-based revalidation and security event monitoring
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { permissionIntegrity, SecurityEvent } from '../../../lib/permissions/services/permission-integrity';
import type { SecurityLevel } from './usePermissionGate';

interface SecurityMonitoringConfig {
  securityLevel: SecurityLevel;
  onSecurityViolation?: (_event: SecurityEvent) => void;
  onVisibilityRevalidation?: () => void;
  revalidationInterval?: number;
}

export const useSecurityMonitoring = ({
  securityLevel,
  onSecurityViolation,
  onVisibilityRevalidation,
  revalidationInterval = 0,
}: SecurityMonitoringConfig) => {
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const revalidationTimerRef = useRef<number | null>(null);
  const [securityViolation, setSecurityViolation] = useState(false);
  const [lastValidation, setLastValidation] = useState<number>(0);

  // Setup visibility-based revalidation
  const setupVisibilityRevalidation = useCallback((containerElement: HTMLElement | null) => {
    if (!containerElement) return;

    intersectionObserverRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Component became visible - revalidate if needed
          const timeSinceLastValidation = Date.now() - lastValidation;
          const revalidationThreshold = securityLevel === 'maximum' ? 30000 : 60000; // 30s or 60s

          if (timeSinceLastValidation > revalidationThreshold) {
            onVisibilityRevalidation?.();
          }
        }
      });
    });

    intersectionObserverRef.current.observe(containerElement);
  }, [lastValidation, securityLevel, onVisibilityRevalidation]);

  // Setup periodic revalidation
  const setupPeriodicRevalidation = useCallback(() => {
    if (revalidationInterval > 0) {
      revalidationTimerRef.current = window.setInterval(() => {
        onVisibilityRevalidation?.();
      }, revalidationInterval);
    }
  }, [revalidationInterval, onVisibilityRevalidation]);

  // Setup security event listener
  const setupSecurityEventListener = useCallback(() => {
    return permissionIntegrity.onSecurityEvent((event: SecurityEvent) => {
      if (event.severity === 'CRITICAL') {
        setSecurityViolation(true);

        if (onSecurityViolation) {
          onSecurityViolation(event);
        }
      }
    });
  }, [onSecurityViolation]);

  // Update last validation timestamp
  const updateLastValidation = useCallback(() => {
    setLastValidation(Date.now());
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }

    if (revalidationTimerRef.current) {
      clearInterval(revalidationTimerRef.current);
      revalidationTimerRef.current = null;
    }
  }, []);

  // Auto-cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    securityViolation,
    lastValidation,
    setupVisibilityRevalidation,
    setupPeriodicRevalidation,
    setupSecurityEventListener,
    updateLastValidation,
    cleanup,
  };
};